/**
 * resolveScreenRefs.ts — плеер-сетка безопасности для ссылок правил (WEB-780).
 *
 * До BE-фикса AI-редактор сохранял правила навигации со ссылками на экраны по
 * ИМЕНИ (`scope_ref: "Screen 1"`, `action.target: "Screen 3"`), тогда как
 * плеер отбирает и исполняет правила сравнением с id экрана
 * (`decide`: `rule.scope_ref === screenId`; GOTO_SCREEN:
 * `screens.findIndex(s => s.id === action.target)`). Такое правило не срабатывало.
 *
 * BE-фикс (resolve-rule-refs.util) приводит ссылки к id для НОВЫХ правил, но на
 * проде уже лежат сломанные (в published/draft logic колонках). Эта функция
 * нормализует ссылки один раз при загрузке плеера, чтобы старые правила
 * заработали без ре-публикации. Fail-open: неразрешимое имя оставляем как есть
 * (правило просто не сработает, движок логики fail-open и не бросает).
 *
 * Чистый модуль: без React/DOM.
 */

import type { Logic } from './rule.schema';

interface ScreenLike {
  id: string;
  name?: string;
}

function norm(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * WEB-3767 — единственное правило «ссылка на экран → код экрана»: им приводит
 * ссылки проигрыватель (`resolveScreenRefs` ниже) и им же окно «Логика»
 * редактора ищет экран правила (`components/editor/logic/logicRefs.ts`,
 * `ruleScreenId`), чтобы окно показывало правило там, где его исполняет плеер.
 * Порядок: код экрана → имя экрана без учёта регистра и пробелов (при повторе
 * имени — первый экран) → `null`.
 */
export function screenRefResolver(
  screens: ReadonlyArray<ScreenLike>,
): (ref: string) => string | null {
  const ids = new Set(screens.map((s) => s.id));
  const byName = new Map<string, string>();
  for (const s of screens) {
    if (s.name && s.name.trim().length > 0 && !byName.has(norm(s.name))) {
      byName.set(norm(s.name), s.id);
    }
  }
  return (ref) => (ids.has(ref) ? ref : (byName.get(norm(ref)) ?? null));
}

export function resolveScreenRefs(
  logic: Logic,
  screens: ReadonlyArray<ScreenLike>,
): Logic {
  if (!Array.isArray(logic) || logic.length === 0) return logic;

  const find = screenRefResolver(screens);
  // id как есть → он же; иначе имя → id; иначе исходная строка (fail-open).
  const toId = (ref: string): string => find(ref) ?? ref;

  return logic.map((rule) => {
    let changed = false;
    const next = { ...rule } as typeof rule & { scope_ref: string };

    if (
      next.trigger_scope === 'screen' &&
      typeof next.scope_ref === 'string'
    ) {
      const resolved = toId(next.scope_ref);
      if (resolved !== next.scope_ref) {
        next.scope_ref = resolved;
        changed = true;
      }
    }

    // WEB-1717 п.3: приведение было только у GOTO_SCREEN. У «перейти к
    // результату» цель — тоже ссылка на экран (просто необязательная), и её
    // имя точно так же надо приводить к id: иначе плеер цель не находит и
    // уносит покупателя на последний экран мимо экрана оплаты. Цель не задана
    // (её нет в объекте) — поле не выдумываем.
    if (
      (next.action?.type === 'GOTO_SCREEN' || next.action?.type === 'GOTO_ENDING') &&
      typeof next.action.target === 'string'
    ) {
      const resolved = toId(next.action.target);
      if (resolved !== next.action.target) {
        next.action = { ...next.action, target: resolved };
        changed = true;
      }
    }

    return changed ? next : rule;
  }) as Logic;
}
